import type { CallState, CallStateConfig } from 'Modules/softphone/types';

import {
  NO_PERMISSIONS,
  ESTABLISHING_PERMISSIONS,
  ONGOING_CALL_PERMISSIONS,
} from './permissions';

/**
 * Complete configuration for all CallStates
 * Single source of truth for UI representation, behavior flags, and permissions
 */
export const CALL_STATE_CONFIG: Record<CallState, CallStateConfig> = {
  idle: {
    label: '',
    color: 'text-muted-foreground',
    flags: {
      isConnected: false,
      isRinging: false,
      isInProgress: false,
      isTerminal: true,
    },
    permissions: NO_PERMISSIONS,
  },
  connecting: {
    label: 'Calling...',
    color: 'text-yellow-500',
    flags: {
      isConnected: false,
      isRinging: false,
      isInProgress: true,
      isTerminal: false,
    },
    permissions: ESTABLISHING_PERMISSIONS,
  },
  ringing: {
    label: 'Calling...',
    color: 'text-blue-500',
    flags: {
      isConnected: false,
      isRinging: true,
      isInProgress: true,
      isTerminal: false,
    },
    permissions: ESTABLISHING_PERMISSIONS,
  },
  active: {
    label: 'Connected',
    color: 'text-green-500',
    flags: {
      isConnected: true,
      isRinging: false,
      isInProgress: true,
      isTerminal: false,
    },
    permissions: ONGOING_CALL_PERMISSIONS,
  },
  held: {
    label: 'Holding',
    color: 'text-warning',
    flags: {
      isConnected: true,
      isRinging: false,
      isInProgress: true,
      isTerminal: false,
    },
    permissions: ONGOING_CALL_PERMISSIONS,
  },
  reconnecting: {
    label: 'Reconnecting...',
    color: 'text-orange-500',
    flags: {
      isConnected: false,
      isRinging: false,
      isInProgress: true,
      isTerminal: false,
    },
    permissions: ESTABLISHING_PERMISSIONS,
  },
  ended: {
    label: 'Call ended',
    color: 'text-muted-foreground',
    flags: {
      isConnected: false,
      isRinging: false,
      isInProgress: false,
      isTerminal: true,
    },
    permissions: NO_PERMISSIONS,
  },
  failed: {
    label: '',
    color: 'text-destructive',
    flags: {
      isConnected: false,
      isRinging: false,
      isInProgress: false,
      isTerminal: true,
    },
    permissions: NO_PERMISSIONS,
  },
  busy: {
    label: '',
    color: 'text-destructive',
    flags: {
      isConnected: false,
      isRinging: false,
      isInProgress: false,
      isTerminal: true,
    },
    permissions: NO_PERMISSIONS,
  },
  'no-answer': {
    label: 'No Answer',
    color: 'text-muted-foreground',
    flags: {
      isConnected: false,
      isRinging: false,
      isInProgress: false,
      isTerminal: true,
    },
    permissions: NO_PERMISSIONS,
  },
};

/**
 * SIP status codes that map to specific CallStates
 *
 * References:
 * - RFC 3261 (SIP: Session Initiation Protocol)
 * - https://en.wikipedia.org/wiki/List_of_SIP_response_codes
 */
export const SIP_STATUS_TO_CALL_STATE: Partial<Record<number, CallState>> = {
  // Client errors (4xx)
  408: 'no-answer', // Request Timeout
  480: 'no-answer', // Temporarily Unavailable
  486: 'busy', // Busy Here
  487: 'ended', // Request Terminated (normal cancel)

  // Server errors (5xx)
  500: 'failed', // Server Internal Error
  503: 'failed', // Service Unavailable

  // Global failures (6xx)
  600: 'busy', // Busy Everywhere
  603: 'ended', // Decline
};

/**
 * Terminal states that indicate call has ended
 */
export const TERMINAL_STATES: readonly CallState[] = [
  'idle',
  'ended',
  'failed',
  'busy',
  'no-answer',
] as const;

/**
 * Check if a call state is terminal (call has ended)
 * Terminal states should not keep the softphone UI visible
 *
 * @param state - The call state to check
 * @returns true if the state is terminal
 */
export const isTerminalState = (state: CallState): boolean => {
  return TERMINAL_STATES.includes(state);
};
